import { Log } from '@sortlist-frontend/mlm';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { isBrowser } from '@sortlist-frontend/utils';
import Head from 'next/head';
import {
  AggregateRating,
  CreativeWork,
  ImageObject,
  LocalBusiness,
  Offer,
  Organization,
  Person,
  Place,
  PostalAddress,
  ProfessionalService,
  QuantitativeValue,
  Rating,
  Review,
  Service,
  Thing,
  UnitPriceSpecification,
  WebPage,
  WebSite,
} from 'schema-dts';

import { usePublicAppContext } from '_core/context/public-app-context';
import { language, locale } from '_core/utils/languages/languages.types';
import { getLanguageTranslator } from '_core/utils/languages/languages.util';

import { generateOrganization, generateWebsite } from './HomepageJsonld';
import { useGetAgency } from './hooks/useGetAgency';
import { escapeJsonLd } from './utils';

const SCHEMA_CONTEXT = 'https://schema.org' as const;

interface AgencyProfileJsonLdProps {
  canonical: string;
  slug: string;
}

export const AgencyProfileJsonLd = ({ canonical, slug }: AgencyProfileJsonLdProps) => {
  const { data } = useGetAgency(slug);
  const { domainInfo, locale } = usePublicAppContext();
  const localeIso = domainInfo?.getIso31661();
  const origin = domainInfo?.getOriginUrl() ?? '';
  const { t } = useTranslation(['common']);

  if (data?.agency == null) return null;

  const { agency } = data;
  const { socialProfiles, foundedAt, prospectAreas, services, reviews, works, awards } = agency;
  const { locality, address } = agency.mainOffice ?? {};

  const inLanguage = localeIso != null ? `${locale}-${localeIso}` : locale;
  const hasOffice = address != null;

  const webPage: WebPage = {
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: `${agency.name} - ${locality?.name != null && locality?.country?.name != null ? locality.name + ', ' + locality.country.name : address}`,
    description: agency.tagline ?? undefined,
    inLanguage,
    isPartOf: {
      '@id': `${origin}#website`,
    },
    about: { '@id': `https://www.sortlist.com#organization` },
    primaryImageOfPage:
      agency.coverPictureUrl != null
        ? {
            '@id': `${canonical}#primaryimage`,
          }
        : undefined,
    image:
      agency.coverPictureUrl != null
        ? {
            '@id': `${canonical}#primaryimage`,
          }
        : undefined,
    dateModified: agency.updatedAt,
    lastReviewed: agency.updatedAt,
    mainEntity: { '@id': `${canonical}#agency` },
    potentialAction: {
      '@type': 'ReadAction',
      target: [canonical],
    },
  };

  const heroImage: ImageObject | undefined =
    agency.coverPictureUrl != null
      ? {
          '@type': 'ImageObject',
          '@id': `${canonical}#primaryimage`,
          url: agency.coverPictureUrl,
          contentUrl: agency.coverPictureUrl,
          caption: `${agency.name} - ${agency.tagline}`,
          // We don't have these
          // width, height
        }
      : undefined;

  const logo: ImageObject | undefined =
    agency.logoUrl != null
      ? {
          '@type': 'ImageObject',
          '@id': `${canonical}#logo`,
          url: agency.logoUrl,
          caption: `${agency.name} logo`,
        }
      : undefined;

  const priceRange =
    services?.nodes
      ?.map((service) => service?.budget as number[])
      .filter((x) => x != null)
      .flat() ?? [];
  const min = priceRange.length > 0 ? Math.min(...priceRange) : undefined;
  const max = priceRange.length > 0 ? Math.max(...priceRange) : undefined;
  const allAwards = awards?.nodes?.map((award) => award?.name).filter((x) => x != null) ?? [];
  const hasWorks = (works?.nodes?.length ?? 0) > 0;

  const creativeWorks: CreativeWork[] = hasWorks
    ? (works?.nodes
        ?.filter((work) => work?.slug != null && work?.name != null)
        ?.map(
          (work) =>
            ({
              '@type': 'CreativeWork',
              '@id': `${canonical}#project-${work?.slug}`,
              name: work?.name ?? undefined,
              description: work?.description ?? undefined,
              creator: {
                '@id': `${canonical}#agency`,
              },
              sourceOrganization:
                work?.company?.name != null
                  ? ({ '@type': 'Organization', name: work?.company?.name } satisfies Organization)
                  : undefined,
              // image missing
              about:
                work?.aspects?.nodes
                  ?.map((aspect) => aspect?.expertise?.name)
                  .filter((name): name is string => name != null)
                  .join(', ') ?? undefined,
            }) satisfies CreativeWork,
        ) ?? [])
    : [];

  const allRatings = reviews?.nodes?.map((review) => review?.rating).filter((x) => x != null) ?? [];
  const hasRatings = reviews?.totalCount != null && allRatings.length > 0 && reviews.averageRating != null;
  const aggregateRating: AggregateRating | undefined = hasRatings
    ? ({
        '@type': 'AggregateRating',
        ratingValue: Math.round(reviews.averageRating! * 5 * 100) / 100,
        reviewCount: reviews?.totalCount ?? 0,
        bestRating: 5,
        worstRating: 1,
        itemReviewed: {
          '@id': `${canonical}#agency`,
        },
      } satisfies AggregateRating)
    : undefined;

  const reviewList: Review[] = hasRatings
    ? (reviews.nodes?.map(
        (review) =>
          ({
            '@type': 'Review',
            author:
              review?.userFullName != null
                ? ({
                    '@type': 'Person',
                    name: review?.userFullName,
                  } satisfies Person)
                : undefined,
            reviewBody: review?.descriptionSummary ?? undefined,
            reviewAspect: review?.service?.expertise?.name ?? undefined,
            itemReviewed: {
              '@id': `${canonical}#agency`,
            },
            reviewRating:
              review?.rating != null
                ? ({
                    '@type': 'Rating',
                    ratingValue: Math.round(review.rating * 5 * 100) / 100,
                  } satisfies Rating)
                : undefined,
          }) satisfies Review,
      ) ?? [])
    : [];

  const places: Place[] | undefined =
    agency.offices?.nodes == null
      ? []
      : agency.offices.nodes
          .filter((office) => office !== null && office.locality?.name != null)
          .map(
            (office) =>
              ({
                '@type': 'Place',
                '@id': `${canonical}#place-${office?.locality?.name?.toLocaleLowerCase()}`,
                name: `${agency.name} - ${office?.locality?.name} - ${t('common:office')}`,
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: office?.address ?? undefined,
                  addressLocality: office?.locality?.name ?? undefined,
                  addressCountry: office?.locality?.country?.iso31661 ?? undefined,
                } satisfies PostalAddress,
              }) satisfies Place,
          )
          .filter((place, index, self) => self.findIndex((p) => p['@id'] === place['@id']) === index);

  const business: LocalBusiness | ProfessionalService = {
    '@type': hasOffice ? 'LocalBusiness' : 'ProfessionalService',
    '@id': `${canonical}#agency`,
    url: canonical,
    name: agency.name,
    description: agency.description ?? undefined,
    image: [agency.logoUrl, agency.coverPictureUrl].filter((x) => x != null),
    foundingDate: foundedAt ?? undefined,
    areaServed: prospectAreas?.nodes?.map((area) => area?.address).filter((x) => x != null),
    knowsAbout: services?.nodes?.map((service) => service?.expertise?.name).filter((x) => x != null),
    makesOffer: services?.nodes?.map((service) => {
      const minBudget = service?.budget?.[0];
      const maxBudget = service?.budget?.[1];
      const priceSpecification: UnitPriceSpecification | undefined =
        minBudget != null && maxBudget != null
          ? {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'EUR',
              minPrice: minBudget,
              maxPrice: maxBudget,
              unitText: 'project',
            }
          : undefined;
      return {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service?.expertise?.name ?? undefined,
          description: service?.description ?? undefined,
          serviceType: service?.expertise?.name ?? undefined,
        } satisfies Service,
        priceSpecification,
      } satisfies Offer;
    }),
    priceRange: min != null && max != null ? `€${min} - €${max} / project` : undefined,
    knowsLanguage:
      agency.languages
        ?.map((lang) => getLanguageTranslator(locale as locale)(lang as language))
        .filter((x) => x != null) ?? undefined,
    numberOfEmployees:
      agency.teamSize != null
        ? ({
            '@type': 'QuantitativeValue',
            value: agency.teamSize,
          } satisfies QuantitativeValue)
        : undefined,
    sameAs: [
      socialProfiles?.facebook,
      socialProfiles?.instagram,
      socialProfiles?.linkedin,
      socialProfiles?.pinterest,
      socialProfiles?.vimeo,
      socialProfiles?.x,
      socialProfiles?.youtube,
    ].filter((x) => x != null),
    address:
      address != null
        ? ({
            '@type': 'PostalAddress',
            streetAddress: address ?? undefined,
            addressLocality: locality?.name ?? undefined,
            addressCountry: locality?.countryIso31661 ?? undefined,
            // no postal code atm
          } satisfies PostalAddress)
        : undefined,
    location: agency.offices?.nodes
      ?.filter((office) => office != null && office.locality?.name != null)
      ?.map((office) => ({
        '@id': `${canonical}#place-${office?.locality?.name?.toLocaleLowerCase()}`,
      })),
    award: allAwards.length > 0 ? allAwards : undefined,
    review: reviewList,
    subjectOf: hasWorks
      ? creativeWorks.map((work) => ({
          '@type': 'CreativeWork',
          '@id': work['@id'],
        }))
      : undefined,
    aggregateRating,
  } satisfies LocalBusiness | ProfessionalService;

  const organization = generateOrganization({ origin, withRating: false });

  const website = generateWebsite({ origin, inLanguage });

  const schema: (
    | WebPage
    | ImageObject
    | Organization
    | WebSite
    | LocalBusiness
    | ProfessionalService
    | CreativeWork
    | Review
    | Place
  )[] = [
    webPage,
    ...(heroImage != null ? [heroImage] : []),
    ...(logo != null ? [logo] : []),
    organization,
    website,
    business,
    ...creativeWorks,
    ...reviewList,
    ...places,
  ];

  const graph: { '@context': string; '@graph': Thing[] } = {
    '@context': SCHEMA_CONTEXT,
    '@graph': schema,
  };

  // Validate schema.org properties server-side only
  if (!isBrowser()) {
    import('./utils').then(({ validateSchemaOrg }) => {
      const errors = validateSchemaOrg(schema);
      if (errors.length > 0) {
        // We don't want to spam sentry issues with these but we still want to monitor the amount of logs
        // eslint-disable-next-line
        Log.error(`Schema.org validation errors for AgencyProfile: ${agency.slug}`, {
          errors: errors.map((err) => ({ type: err.type, path: err.path, message: err.message })),
        });
      }
    });
  }

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: escapeJsonLd(JSON.stringify(graph)) }} />
    </Head>
  );
};
